import {
	BarChartOutlined,
	FundOutlined,
	TableOutlined,
	RiseOutlined,
	TeamOutlined,
	InboxOutlined,
} from '@ant-design/icons';
import { type ComponentType } from 'react';

export interface HierarchyEntityType {
	id: number;
	name: string;
	parentId?: number | null;
}

export const clinicFlagKeys: readonly ['hasQualityProduct', 'hasRiskProduct', 'clinicWithCoders'] = [
	'hasQualityProduct',
	'hasRiskProduct',
	'clinicWithCoders',
];

type ClinicTypes = (typeof clinicFlagKeys)[number];

export type ClinicFlags = {
	[key in ClinicTypes]: boolean;
};

type ClinicPermissions = {
	id: number;
} & ClinicFlags;

export type ClinicsPermissions = ClinicPermissions[];

export type TabNames =
	| 'Engagement hub'
	| 'Provider engagement'
	| 'Quality engagement'
	| 'Patients'
	| 'Risk adjustment'
	| 'Quality'
	| 'Provider actions reports'
	| 'Actions reports'
	| "Last year's reports"
	| 'Risk patient report'
	| 'Actions on patients'
	| 'Provider actions'
	| 'Coder actions'
	| 'Quality patient report'
	| 'Quality patients'
	| 'Overview'
	| 'Measure performance'
	| 'Provider performance'
	| 'Payer performance';

export const DashboardNames = [
	'engagement_hub',
	'care_team_engagement',
	'patient_risk_gaps',
	'risk_adjustment',
	'detailed_actions_report_provider',
	'detailed_actions_report_coder',
	'patient_level_report_risk',
	'patient_level_report_quality',
	'quality_overview',
	'quality_measures_breakdown',
	'quality_provider',
	'quality_payers',
	'detailed_actions_report_provider_last_year',
	'detailed_actions_report_coder_last_year',
	'patient_risk_gaps_last_year',
];

export type DashboardNamesType = (typeof DashboardNames)[number];

export type BadgeOptions = 'BETA' | 'NEW';

interface DashboardTabsDataTypeBase {
	tabName: TabNames;
	requiredPermissions: ClinicTypes[];
	excludedByPermissions?: ClinicTypes[];
	icon: ComponentType;
	featureFlag?: keyof FeatureFlags;
	badge?: BadgeOptions;
}

type DashboardTabsDataTypeWithDashboardName = DashboardTabsDataTypeBase & {
	dashboardName: DashboardNamesType;
	children?: never;
};

type DashboardTabsDataTypeWithChildren = DashboardTabsDataTypeBase & {
	dashboardName?: never;
	children: (Required<
		Omit<
			DashboardTabsDataType,
			'requiredPermissions' | 'excludedByPermissions' | 'children' | 'icon' | 'featureFlag' | 'badge'
		>
	> & { badge?: BadgeOptions; featureFlag?: keyof FeatureFlags })[];
};

type DashboardTabsDataType = DashboardTabsDataTypeWithDashboardName | DashboardTabsDataTypeWithChildren;
export const dashboardTabsData: DashboardTabsDataType[] = [
	{
		tabName: 'Engagement hub',
		dashboardName: 'engagement_hub',
		requiredPermissions: ['hasRiskProduct'],
		excludedByPermissions: ['hasQualityProduct'],
		icon: FundOutlined,
	},
	{
		tabName: 'Engagement hub',
		requiredPermissions: ['hasQualityProduct', 'hasRiskProduct'],
		icon: FundOutlined,
		children: [
			{
				tabName: 'Provider engagement',
				dashboardName: 'engagement_hub',
			},
			{
				tabName: 'Quality engagement',
				dashboardName: 'care_team_engagement',
			},
		],
	},
	{
		tabName: 'Quality engagement',
		dashboardName: 'care_team_engagement',
		requiredPermissions: ['hasQualityProduct'],
		excludedByPermissions: ['hasRiskProduct'],
		icon: FundOutlined,
	},
	{
		tabName: 'Patients',
		dashboardName: 'patient_risk_gaps',
		requiredPermissions: ['hasRiskProduct'],
		icon: TeamOutlined,
		featureFlag: 'flag_AnalyticsPatientsTab',
	},
	{
		tabName: 'Risk adjustment',
		dashboardName: 'risk_adjustment',
		requiredPermissions: ['hasRiskProduct'],
		icon: BarChartOutlined,
	},
	{
		tabName: 'Quality',
		requiredPermissions: ['hasQualityProduct'],
		icon: RiseOutlined,
		children: [
			{
				tabName: 'Overview',
				dashboardName: 'quality_overview',
			},
			{
				tabName: 'Measure performance',
				dashboardName: 'quality_measures_breakdown',
			},
			{
				tabName: 'Provider performance',
				dashboardName: 'quality_provider',
			},
			{
				tabName: 'Payer performance',
				dashboardName: 'quality_payers',
			},
			{
				tabName: 'Quality patients',
				dashboardName: 'patient_level_report_quality',
			},
		],
	},
	{
		tabName: 'Actions reports',
		requiredPermissions: ['hasRiskProduct', 'clinicWithCoders'],
		icon: TableOutlined,
		children: [
			{
				tabName: 'Provider actions',
				dashboardName: 'detailed_actions_report_provider',
			},
			{
				tabName: 'Coder actions',
				dashboardName: 'detailed_actions_report_coder',
			},
			{
				tabName: 'Actions on patients',
				dashboardName: 'patient_level_report_risk',
			},
		],
	},
	{
		tabName: 'Actions reports',
		requiredPermissions: ['hasRiskProduct'],
		excludedByPermissions: ['clinicWithCoders'],
		icon: TableOutlined,
		children: [
			{
				tabName: 'Provider actions',
				dashboardName: 'detailed_actions_report_provider',
			},
			{
				tabName: 'Actions on patients',
				dashboardName: 'patient_level_report_risk',
			},
		],
	},
	{
		tabName: "Last year's reports",
		requiredPermissions: ['hasRiskProduct', 'clinicWithCoders'],
		icon: InboxOutlined,
		children: [
			{
				tabName: 'Provider actions',
				dashboardName: 'detailed_actions_report_provider_last_year',
			},
			{
				tabName: 'Coder actions',
				dashboardName: 'detailed_actions_report_coder_last_year',
			},
			{
				tabName: 'Patients',
				dashboardName: 'patient_risk_gaps_last_year',
				featureFlag: 'flag_AnalyticsPatientsTab',
			},
		],
	},
	{
		tabName: "Last year's reports",
		requiredPermissions: ['hasRiskProduct'],
		excludedByPermissions: ['clinicWithCoders'],
		icon: InboxOutlined,
		children: [
			{
				tabName: 'Provider actions',
				dashboardName: 'detailed_actions_report_provider_last_year',
			},
			{
				tabName: 'Patients',
				dashboardName: 'patient_risk_gaps_last_year',
				featureFlag: 'flag_AnalyticsPatientsTab',
			},
		],
	},
];

export interface GetQuickSightEmbedURLInputType {
	dashboardNames: DashboardNamesType[];
}
export interface GetQuickSightEmbedURLOutputType {
	data: {
		embedUrl?: string;
		clinicsPermissions: ClinicsPermissions;
		dashboardNamesToIds: Record<DashboardNamesType, string>;
	};
}

export interface ApiGatewayType {
	getQuicksightEmbedURL: (params: GetQuickSightEmbedURLInputType) => Promise<GetQuickSightEmbedURLOutputType>;
}

export interface AnalyticsType {
	track: (analyticsType: string, properties?: Record<string, unknown>) => void;
}

export enum DashboardState {
	Loading,
	NoData,
	Error,
	NeedsRefresh,
	Success,
}

export type QuickSightParameterName = 'IsMSO' | 'DataSourceID';
export type QuickSightParameters = { Name: QuickSightParameterName; Values: string[] }[];

export interface TreeNode {
	id: string;
	pId?: string;
	value: string;
	title: string;
}
export type AllTreeNode = TreeNode & { id: 'all'; value: 'all' };

export interface FeatureFlags {
	flag_AnalyticsPatientsTab: boolean;
}
